import { IRideDto } from "../../dto";
import { isDateTimeInPast } from "../date.helpers";

/**
 * External "taxi" providers (mirrors the backend PROVIDERS_REQUIRING_PASSENGERS set). These are the
 * values stored in `externalMetadata.provider` when a ride was dispatched to one of these providers.
 */
export const TAXI_SUPPLIER_PROVIDERS: ReadonlySet<string> = new Set([
  "GETT",
  "YANGO",
  "KASTEL",
  "ISR",
]);

/**
 * Lead time (minutes from now) applied when duplicating a past taxi ride to re-dispatch it to a new
 * supplier — the new ride starts NEW_SUPPLIER_RIDE_LEAD_MINUTES from now so it is no longer in the
 * past (RYDE-7633).
 */
export const NEW_SUPPLIER_RIDE_LEAD_MINUTES = 20;

/** True when the given provider string is one of the taxi providers (Gett/Yango/Kastel/ISR). */
export const isTaxiProvider = (provider: string | undefined | null): boolean =>
  Boolean(provider && TAXI_SUPPLIER_PROVIDERS.has(provider));

/**
 * True when a ride was dispatched to a taxi provider (Gett/Yango/Kastel/ISR) AND its start time is
 * already in the past. Those providers reject any create/update/cancel for a past ride, so once this
 * holds the dashboard must (RYDE-7633): forbid changing the supplier, collect a free-text reason on
 * cancel (cancelled locally, no provider notification), and offer re-dispatch only via duplication.
 *
 * This is a UX affordance — the backend enforces the same rules authoritatively.
 */
export const isPastTaxiSupplierRide = (
  ride: Partial<IRideDto> | undefined | null,
): boolean => {
  // Keyed on externalMetadata.provider — the provider the ride was actually DISPATCHED to. Governs
  // the read-only lock (item 1-4): once a ride was sent to a taxi supplier and has started, it is
  // locked. Do not use this for the live "can I send the currently-selected supplier" decision —
  // use isTaxiProvider on the selected supplier's provider for that.
  if (!isTaxiProvider(ride?.externalMetadata?.provider)) {
    return false;
  }

  return isDateTimeInPast(ride?.rideStart);
};

/**
 * True when at least one of the given rides is a past taxi-supplier ride — used by the bulk cancel
 * entry points (bulk operations drawer, grid status dropdown) to collect the free-text reason once
 * for the whole selection, since the backend cancels those rides locally and requires it (RYDE-7633).
 */
export const hasPastTaxiSupplierRide = (
  rides: (Partial<IRideDto> | undefined | null)[] | undefined | null,
): boolean => Boolean(rides?.some(ride => isPastTaxiSupplierRide(ride)));
